import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { lg } from '../common';

// Register custom easing curve
CustomEase.create('lineEase', '0.3, 0, 0.7, 1');

const ANIMATION_DURATION = 1.1;
const ANIMATION_EASE = 'lineEase';
const GRADIENT_STOPS = 'rgb(255 48 0 / 0%) 5.61%, #ff3000 85.91%';

const SCHEMA_SEQUENCES = {
  'load-testing': [
    { btns: ['har'], lines: ['har'] },
    { btns: ['jmeter'], lines: ['jmeter'] },
    { btns: ['postman'], lines: ['postman'] },
    { btns: ['grafana'], lines: ['grafana'] },
    { btns: ['ai'], lines: ['ai'] },
    { btns: ['reports'], lines: ['reports'] },
    { btns: [], lines: [] },
  ],
  'gen-load': [
    { btns: ['google-analytics'], lines: [] },
    { btns: ['google-analytics', 'load-generator'], lines: ['load-generator'] },
    { btns: ['load-generator', 'web'], lines: ['web'] },
    { btns: ['load-generator', 'api'], lines: ['api'] },
    { btns: ['load-generator'], lines: ['diagram'] },
    { btns: ['reports'], lines: ['reports'] },
    { btns: ['dashboard'], lines: ['dashboard'] },
    { btns: [], lines: [] },
  ],
  'cloud-power': [
    { btns: ['har'], lines: ['har'] },
    { btns: ['jmeter'], lines: ['jmeter'] },
    { btns: ['postman'], lines: ['postman'] },
    { btns: ['ai'], lines: ['ai'] },
    { btns: [], lines: [] },
  ],
  repeat: [
    { btns: ['load-generator'], lines: ['load-generator'] },
    { btns: ['load-generator', 'dashboard'], lines: ['dashboard'] },
    { btns: ['dashboard', 'ai'], lines: ['ai'] },
    { btns: ['load-generator', 'grafana'], lines: ['grafana'] },
    { btns: [], lines: [] },
  ],
};

const LINES_ANIMATIONS = {
  'load-testing': {
    har: {
      maskImage: new URL(
        '/assets/images/landing-scroller/load-testing-har-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        y: '-25%',
        x: '-25%',
        gradientDirection: 'to right bottom',
      },
      animate: {
        width: '50%',
        height: '50%',
        x: '100%',
        y: '100%',
      },
    },
    jmeter: {
      maskImage: new URL(
        '/assets/images/landing-scroller/load-testing-jmeter-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        top: 0,
        left: 0,
        width: 0,
        height: '100%',
        x: '-25%',
        gradientDirection: 'to right',
      },
      animate: {
        width: '50%',
        x: '100%',
      },
    },
    postman: {
      maskImage: new URL(
        '/assets/images/landing-scroller/load-testing-postman-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: 0,
        left: 0,
        width: 0,
        height: 0,
        x: '-25%',
        y: '25%',
        gradientDirection: 'to right top',
      },
      animate: {
        width: '50%',
        height: '50%',
        x: '100%',
        y: '-100%',
      },
    },
    grafana: {
      maskImage: new URL(
        '/assets/images/landing-scroller/load-testing-grafana-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: '25%',
        left: '25%',
        width: 0,
        height: 0,
        x: '-25%',
        y: '25%',
        gradientDirection: 'to right top',
      },
      animate: {
        width: '50%',
        height: '50%',
        x: '100%',
        y: '-100%',
      },
    },
    ai: {
      maskImage: new URL(
        '/assets/images/landing-scroller/load-testing-ai-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        top: 0,
        left: '25%',
        width: 0,
        height: '100%',
        x: '-25%',
        gradientDirection: 'to right',
      },
      animate: {
        width: '50%',
        x: '100%',
      },
    },
    reports: {
      maskImage: new URL(
        '/assets/images/landing-scroller/load-testing-reports-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        top: '25%',
        left: '25%',
        width: 0,
        height: 0,
        x: '-25%',
        y: '-25%',
        gradientDirection: 'to right bottom',
      },
      animate: {
        width: '50%',
        height: '50%',
        x: '100%',
        y: '100%',
      },
    },
  },
  'gen-load': {
    'load-generator': {
      maskImage: new URL(
        '/assets/images/landing-scroller/gen-load-load-generator-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        top: 0,
        left: 0,
        width: 0,
        height: '100%',
        x: '-25%',
        gradientDirection: 'to right',
      },
      animate: {
        width: '80%',
        x: '100%',
      },
    },
    web: {
      maskImage: new URL(
        '/assets/images/landing-scroller/gen-load-web-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        top: 0,
        left: 0,
        width: 0,
        height: '100%',
        x: '-25%',
        gradientDirection: 'to right',
      },
      animate: {
        width: '80%',
        x: '50%',
      },
    },
    api: {
      maskImage: new URL(
        '/assets/images/landing-scroller/gen-load-api-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: 0,
        right: 0,
        width: 0,
        height: '100%',
        x: '20%',
        gradientDirection: 'to left',
      },
      animate: {
        width: '50%',
        x: '-50%',
      },
    },
    diagram: {
      maskImage: new URL(
        '/assets/images/landing-scroller/gen-load-diagram-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: '20%',
        right: '32%',
        width: 0,
        height: 0,
        x: '25%',
        y: '25%',
        gradientDirection: 'to left top',
      },
      animate: {
        width: '50%',
        height: '50%',
        x: '-20%',
        y: '-50%',
      },
    },
    reports: {
      maskImage: new URL(
        '/assets/images/landing-scroller/gen-load-reports-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: '70%',
        right: '32%',
        width: 0,
        height: 0,
        x: '25%',
        y: '25%',
        gradientDirection: 'to left top',
      },
      animate: {
        width: '50%',
        height: '50%',
        x: '-20%',
        y: '-50%',
      },
    },
    dashboard: {
      maskImage: new URL(
        '/assets/images/landing-scroller/gen-load-dashboard-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: '70%',
        left: '32%',
        width: 0,
        height: 0,
        x: '-25%',
        y: '25%',
        gradientDirection: 'to right top',
      },
      animate: {
        width: '50%',
        height: '50%',
        x: '20%',
        y: '-50%',
      },
    },
  },
  'cloud-power': {
    har: {
      maskImage: new URL(
        '/assets/images/landing-scroller/cloud-power-har-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: 0,
        left: 0,
        width: 0,
        height: 0,
        x: '-25%',
        y: '25%',
        gradientDirection: 'to right top',
      },
      animate: {
        width: '50%',
        height: '50%',
        x: '50%',
        y: '-50%',
      },
    },
    jmeter: {
      maskImage: new URL(
        '/assets/images/landing-scroller/cloud-power-jmeter-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: 0,
        right: 0,
        width: 0,
        width: '100%',
        height: 0,
        y: '25%',
        gradientDirection: 'to top',
      },
      animate: {
        height: '50%',
        y: '-100%',
      },
    },
    postman: {
      maskImage: new URL(
        '/assets/images/landing-scroller/cloud-power-postman-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: 0,
        right: 0,
        width: 0,
        height: 0,
        x: '25%',
        y: '25%',
        gradientDirection: 'to top left',
      },
      animate: {
        width: '50%',
        height: '50%',
        x: '-50%',
        y: '-50%',
      },
    },
    ai: {
      maskImage: new URL(
        '/assets/images/landing-scroller/cloud-power-ai-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: '50%',
        left: 0,
        width: '100%',
        height: 0,
        y: '25%',
        gradientDirection: 'to top',
      },
      animate: {
        height: '50%',
        y: '-50%',
      },
    },
  },
  repeat: {
    'load-generator': {
      maskImage: new URL(
        '/assets/images/landing-scroller/repeat-load-generator-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: '10%',
        right: '30%',
        width: 0,
        height: 0,
        y: '25%',
        x: '25%',
        gradientDirection: 'to left top',
      },
      animate: {
        width: '100%',
        height: '100%',
        x: '-50%',
        y: '-50%',
      },
    },
    dashboard: {
      maskImage: new URL(
        '/assets/images/landing-scroller/repeat-dashboard-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: '50%',
        left: '10%',
        width: 0,
        height: '100%',
        x: '-25%',
        gradientDirection: 'to right',
      },
      animate: {
        width: '50%',
        x: '100%',
      },
    },
    ai: {
      maskImage: new URL(
        '/assets/images/landing-scroller/repeat-ai-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: '50%',
        left: '50%',
        width: 0,
        height: '100%',
        x: '-25%',
        gradientDirection: 'to right',
      },
      animate: {
        width: '50%',
        x: '100%',
      },
    },
    grafana: {
      maskImage: new URL(
        '/assets/images/landing-scroller/repeat-grafana-mask.svg',
        import.meta.url,
      ).href,
      initial: {
        bottom: '50%',
        left: 0,
        width: 0,
        height: 0,
        y: '25%',
        x: '-25%',
        gradientDirection: 'to right top',
      },
      animate: {
        width: '50%',
        height: '50%',
        x: '100%',
        y: '-100%',
      },
    },
  },
};

function initSchemaAnimation(schema) {
  if (!schema) return;

  const buttons = schema.querySelectorAll('.schema__button');
  const lines = {};
  const animations = {};
  let isRunning = false;

  schema.querySelectorAll('.schema__lines').forEach((line) => {
    if (line.dataset.item) {
      lines[line.dataset.item] = line;
      const after = line.querySelector('div');
      if (after) {
        const schemaName = schema?.dataset?.schema;
        const animationConfig =
          LINES_ANIMATIONS[schemaName]?.[line.dataset.item];
        if (!animationConfig?.initial)
          console.warn(`Animation config for ${schemaName} not found`);
        const initial = {
          ...animationConfig.initial,
          backgroundImage: `linear-gradient(${animationConfig.initial.gradientDirection}, ${GRADIENT_STOPS})`,
        };

        animations[line.dataset.item] = gsap.to(after, {
          ...animationConfig.animate,
          duration: ANIMATION_DURATION,
          ease: ANIMATION_EASE,
          paused: true,
          onStart: () => line.classList.add('is-animating'),
          onComplete: () => line.classList.remove('is-animating'),
          onReverseComplete: () => line.classList.remove('is-animating'),
        });
        if (animationConfig?.maskImage)
          gsap.set(line, { maskImage: `url(${animationConfig.maskImage})` });
        gsap.set(after, initial);
      }
    }
  });

  // Auto-loop animation
  const schemaName = schema.dataset.schema;
  if (!schemaName) return;

  const sequence = SCHEMA_SEQUENCES[schemaName];
  if (!sequence) return;

  let currentStep = 0;

  const clear = () => {
    // Clear all active states
    buttons.forEach((button) => button.classList.remove('is-active'));
    Object.values(animations).forEach((anim) => anim.pause(0));
    Object.values(lines).forEach((line) =>
      line.classList.remove('is-animating'),
    );
  };

  const animateNextStep = () => {
    clear();
    if (!isRunning) {
      currentStep = 0;
      return;
    }
    if (sequence.length === 0) return;

    const step = sequence[currentStep];

    // Set active buttons
    step.btns.forEach((btnId) => {
      const btn = Array.from(buttons).find((b) => b.dataset.item === btnId);
      if (btn) btn.classList.add('is-active');
    });

    // Play line animations
    step.lines.forEach((lineId) => {
      if (animations[lineId]) {
        animations[lineId].restart();
      }
    });

    // Move to next step (loop back to 0 if at end)
    currentStep = (currentStep + 1) % sequence.length;

    // Schedule next step - use longer delay if we just showed the last item
    const delay = currentStep === 0 ? 2000 : 1000;
    setTimeout(animateNextStep, delay);
  };

  return {
    start: () => {
      isRunning = true;
      setTimeout(animateNextStep, 250);
    },
    stop: () => {
      isRunning = false;
      clear();
    },
  };
}

function loadSchemas(sections) {
  const fetchPromises = sections.map((section) => {
    const schemaUrl = section.getAttribute('data-schema-url');
    const innerElement = section.querySelector(
      '.section-scroller__schema-inner',
    );

    if (!schemaUrl || !innerElement) {
      return Promise.resolve(); // Skip if missing URL or inner element
    }

    return fetch(schemaUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch ${schemaUrl}: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        innerElement.innerHTML = html;
      })
      .catch((error) => {
        console.error('Error loading schema:', error);
      });
  });
  return Promise.all(fetchPromises);
}

function setupAnimations(sections) {
  const schemaAnimations = [];
  sections.forEach((section, index) => {
    const schema = section.querySelector('.schema');
    const schemaBtns = schema.querySelectorAll('.schema__button .cell-bg');
    const prevSection = sections[index - 1];
    const prevSchema = prevSection?.querySelector('.schema');
    const prevSchemaBtns = prevSchema
      ? prevSchema.querySelectorAll('.schema__button .cell-bg')
      : [];
    const heading = section.querySelector('.section-scroller__heading');
    const schemaAnimation = initSchemaAnimation(schema);
    schemaAnimations.push(schemaAnimation);
    const prevSchemaAnimation = schemaAnimations[index - 1];

    const updateDimensions = () => {
      const layout = section.querySelector('.section-scroller__layout');
      const main = schema.querySelector('.schema__main');
      section.style.setProperty(
        '--layout-height',
        `${layout.getBoundingClientRect().height}px`,
      );
      section.style.setProperty(
        '--schema-height',
        `${main.getBoundingClientRect().height}px`,
      );
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    gsap.set(schema, { opacity: 0 });
    schemaBtns.forEach((btn) =>
      gsap.set(btn, {
        attr: { transform: 'scale(0.5)' },
      }),
    );

    const mm = gsap.matchMedia();
    mm.add(`(max-width: ${lg - 1}px)`, () => {
      ScrollTrigger.create({
        trigger: schema,
        start: 'top 65%',
        // markers: true,
        onEnter: () => {
          gsap.to(schema, { opacity: 1, duration: 0.25 });
          schemaBtns.forEach((btn) =>
            gsap.set(btn, { attr: { transform: 'scale(1)' } }),
          );
          schemaAnimation.start();
        },
      });

      return () => {
        schemaAnimation.stop();
      };
    });

    mm.add(`(min-width: ${lg}px)`, () => {
      gsap.timeline({
        scrollTrigger: {
          trigger: heading,
          start: 'top 65%',
          toggleActions:
            index === 0 ? 'play none none none' : 'play none none reverse',
          // markers: index === 1,
          onEnter: () => {
            // console.log('onEnter', index);
            if (prevSchema) {
              gsap.set(prevSection, { zIndex: 0 });
              gsap.to(prevSchema, { opacity: 0, duration: 0.25 });
              prevSchemaBtns.forEach((btn) =>
                gsap.set(btn, { attr: { transform: 'scale(0.5)' } }),
              );
              prevSchemaAnimation.stop();
            }
            gsap.set(section, { zIndex: 1 });
            gsap.to(schema, { opacity: 1, duration: 0.25 });
            schemaBtns.forEach((btn) =>
              gsap.set(btn, { attr: { transform: 'scale(1)' } }),
            );
            schemaAnimation.start();
          },
          // onLeave: () => console.log('onLeave', index),
          // onEnterBack: () => console.log('onEnterBack', index),
          onLeaveBack: () => {
            // console.log('onLeaveBack', index);
            if (prevSchema) {
              gsap.set(prevSection, { zIndex: 1 });
              gsap.to(prevSchema, { opacity: 1, duration: 0.25 });
              prevSchemaBtns.forEach((btn) =>
                gsap.set(btn, { attr: { transform: 'scale(1)' } }),
              );
              prevSchemaAnimation.start();
            }
            gsap.set(section, { zIndex: 0 });
            schemaBtns.forEach((btn) =>
              gsap.set(btn, { attr: { transform: 'scale(0.5)' } }),
            );
            gsap.to(schema, { opacity: 0, duration: 0.25 });
            schemaAnimation.stop();
          },
        },
      });

      return () => {
        schemaAnimation.stop();
      };
    });
  });
}

export async function initSectionScroller() {
  const parent = document.querySelector('.section-scroller');
  if (!parent) return;

  ScrollTrigger.create({
    trigger: parent,
    start: 'top-=1000 bottom',
    once: true,
    onEnter: async () => {
      const sections = [
        ...parent.querySelectorAll('.section-scroller__subsection'),
      ];
      await loadSchemas(sections);
      setupAnimations(sections);
    },
  });
}
